import type { DraggableProvidedDraggableProps, DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';

import { validators } from '@connected/api-client/generated/validatorsv2.generated';
import FieldInfo from '@connected/form-fields/components/field-info';
import { NumberField } from '@connected/form-fields/components/number';
import { SingleOptionField, SingleOptionItem } from '@connected/form-fields/components/option';
import { Button } from '@connected/ui/components/button';
import { TableCell } from '@connected/ui/components/table';
import { cn } from '@connected/ui/lib/utils';
import { GripVertical, Trash2 } from 'lucide-react';

import { ObservationComponent } from '@/components/features/observation';
import { showConfirm } from '@/stores/confirmer';

import type { RisoMeasurementRowProps } from '../utils/riso-measurement-types';

import { useDeleteRisoMeasurement, useRisoMeasurementPatcher } from '../hooks/use-riso-measurement-mutations';
import { circuitKindOptions } from '../utils/riso-measurement-constants';

type RisoMeasurementRowComponentProps = RisoMeasurementRowProps & {
    innerRef?: (element: HTMLElement | null) => void;
    dragHandleProps?: DraggableProvidedDragHandleProps | null;
    draggableProps?: DraggableProvidedDraggableProps;
    isDragging?: boolean;
    isDragDropEnabled?: boolean;
};

export function RisoMeasurementRow({
    innerRef,
    index,
    electricalProjectRisoMeasurementId,
    electricalProjectVersionId,
    form,
    dragHandleProps,
    draggableProps,
    isDragging = false,
    isDragDropEnabled = false,
}: RisoMeasurementRowComponentProps) {
    const { mutation: deleteMeasurementMutation } = useDeleteRisoMeasurement({
        electricalProjectVersionId,
    });

    const patcher = useRisoMeasurementPatcher({
        electricalProjectVersionId,
        electricalProjectRisoMeasurementId,
    });

    return (
        <>
            <tr
                ref={innerRef}
                {...draggableProps}
                className={cn(
                    'border-b transition-colors data-[state=selected]:bg-muted',
                    { 'opacity-50 bg-muted': isDragging },
                )}
            >
                {isDragDropEnabled && (
                    <TableCell className="w-10">
                        <div
                            {...dragHandleProps}
                            className="flex items-center justify-center cursor-grab active:cursor-grabbing"
                        >
                            <GripVertical className="h-4 w-4 text-gray-400" />
                        </div>
                    </TableCell>
                )}
                <TableCell>
                    <form.Field
                        name={`measurements[${index}].circuitKind`}
                        validators={{
                            onChange: validators.patchElectricalProjectRisoMeasurementCommand.circuitKind,
                            onChangeAsync: patcher.patchField('CircuitKind'),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {({ handleChange, state, name }) => (
                            <>
                                <SingleOptionField
                                    name={name}
                                    handleChange={handleChange}
                                    className="w-full"
                                    state={state}
                                    options={circuitKindOptions?.map(option => ({
                                        value: option.value,
                                        label: option.label,
                                    })) || []}
                                    render={SingleOptionItem}
                                    dropdown
                                    closeOnClick
                                />
                                <FieldInfo state={state} />
                            </>
                        )}
                    </form.Field>
                </TableCell>
                <TableCell>
                    <form.Field
                        name={`measurements[${index}].measuredValue`}
                        validators={{
                            onChange:
                            validators.patchElectricalProjectRisoMeasurementCommand.measuredValue,
                            onChangeAsync: patcher.patchField('MeasuredValue'),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {({ handleChange, state, name }) => (
                            <>
                                <NumberField
                                    name={name}
                                    onChange={handleChange}
                                    className="w-full"
                                    value={state.value}
                                    placeholder="Gemeten waarde"
                                />
                                <FieldInfo state={state} />
                            </>
                        )}
                    </form.Field>
                </TableCell>
                <TableCell>
                    <form.Field
                        name={`measurements[${index}].minimumValue`}
                        validators={{
                            onChange:
                            validators.patchElectricalProjectRisoMeasurementCommand.minimumValue,
                            onChangeAsync: patcher.patchField('MinimumValue'),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {({ handleChange, state, name }) => (
                            <>
                                <NumberField
                                    name={name}
                                    onChange={handleChange}
                                    className="w-full"
                                    value={state.value}
                                    placeholder="Minimale waarde"
                                />
                                <FieldInfo state={state} />
                            </>
                        )}
                    </form.Field>
                </TableCell>
                <TableCell>
                    <form.Field
                        name={`measurements[${index}].isFaulty`}
                        validators={{
                            onChange:
                            validators.patchElectricalProjectRisoMeasurementCommand.isFaulty,
                            onChangeAsync: patcher.patchField('IsFaulty'),
                            onChangeAsyncDebounceMs: patcher.patchField.debounceTime,
                        }}
                    >
                        {({ handleChange, state, name }) => (
                            <>
                                <SingleOptionField
                                    name={name}
                                    handleChange={handleChange}
                                    state={state}
                                    options={[
                                        { value: false, label: 'Ja' },
                                        { value: true, label: 'Nee' },
                                    ]}
                                    render={SingleOptionItem}
                                    dropdown
                                    closeOnClick
                                />
                                <FieldInfo state={state} />
                            </>
                        )}
                    </form.Field>
                </TableCell>
                <TableCell className="w-10">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={async () => {
                            if (await showConfirm('Weet je zeker dat je wilt verwijderen?')) {
                                deleteMeasurementMutation.mutate({
                                    params: {
                                        query: {
                                            ElectricalProjectRisoMeasurementId: electricalProjectRisoMeasurementId,
                                        },
                                    },
                                });
                            }
                        }}
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                </TableCell>
            </tr>
            <form.Subscribe selector={state => state?.values?.measurements[index]?.isFaulty}>
                {isFaulty => isFaulty && (
                    <tr className="border-b">
                        <td colSpan={6} className="p-4 py-2">
                            <ObservationComponent
                                objectKind="RisoMeasurement"
                                electricalProjectVersionId={electricalProjectVersionId}
                                objectId={electricalProjectRisoMeasurementId}
                            />
                        </td>
                    </tr>
                )}
            </form.Subscribe>
        </>
    );
}
